import React, { useCallback, useEffect, useState } from 'react';
import Tooltip from 'antd/lib/tooltip';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { request } from '../services/utilities';
import { messageService } from '../services/message';
import { DISCHARGE_CLEARANCE_LABELS } from '../permission-utils/discharge';

const DischargeBlock = ({
	patient,
	startDischarge,
	finishDischarge,
	startNicuDischarge,
	finishNicuDischarge,
	location,
	canAdmit,
	canDischarge,
}) => {
	const user = useSelector(state => state.user.profile);
	const [summary, setSummary] = useState(null);

	// a discharge can only be finished once nursing, pharmacy, lab and accounts have all cleared
	const admission = patient?.admission;
	const admissionId = admission?.id;
	const dischargeStarted = !!admission?.start_discharge;

	const fetchClearances = useCallback(async () => {
		if (!admissionId || !dischargeStarted) {
			setSummary(null);
			return;
		}
		try {
			const rs = await request(`patient/admissions/${admissionId}/discharge-clearances`, 'GET', true);
			// admissions started before this workflow have no rows — leave them ungated
			setSummary(rs?.success ? rs.clearance_summary : null);
		} catch (e) {
			setSummary(null);
		}
	}, [admissionId, dischargeStarted]);

	useEffect(() => {
		fetchClearances();
	}, [fetchClearances]);

	useEffect(() => {
		const subscription = messageService.getMessage().subscribe(message => {
			if (message.text?.type === 'discharge-clearance') {
				fetchClearances();
			}
		});
		return () => subscription.unsubscribe();
	}, [fetchClearances]);

	const blockedByClearance = !!summary && summary.status !== 'cleared';
	const pendingLabel = (summary?.pending_departments || []).map(d => DISCHARGE_CLEARANCE_LABELS[d] || d).join(', ');

	return (
		<>
			{(patient?.admission_id && patient?.admission_id === patient?.admission?.id) ||
			(patient?.nicu_id && patient?.nicu_id === patient?.nicu?.id)
				? canDischarge && (
						<>
							{patient?.admission?.id && (
								<>
									{patient?.admission?.start_discharge ? (
										<Tooltip
											title={
												blockedByClearance
													? `Awaiting clearance from: ${pendingLabel}`
													: summary
													? 'All departments cleared — complete discharge'
													: 'Complete Discharge'
											}
										>
											<button
												className={`btn btn-${blockedByClearance ? 'secondary' : 'warning'} btn-sm mr-1`}
												disabled={blockedByClearance}
												onClick={() => !blockedByClearance && finishDischarge(patient?.admission?.id)}
											>
												<i className={`fa fa-${blockedByClearance ? 'lock' : 'hospital-o'}`}></i>
												<span style={{ marginLeft: '4px' }}>{blockedByClearance ? 'Uncleared' : 'Finish Discharge'}</span>
											</button>
										</Tooltip>
									) : (
										<>
											{user.role.slug === 'doctor' || user.role.slug === 'it-admin' || user.role.slug === 'medical-director' ? (
												<Tooltip title="Discharge">
													<button className="btn btn-danger btn-sm mr-1" onClick={() => startDischarge(patient?.admission?.id)}>
														<i className="fa fa-hospital-o"></i>
														<span style={{ marginLeft: '4px' }}>Discharge</span>
													</button>
												</Tooltip>
											) : (
												<Tooltip title="A doctor should start the discharge process">
													<button className="btn btn-danger btn-sm mr-1">
														<i className="fa fa-hospital-o"></i>
														<span style={{ marginLeft: '4px' }}>Discharge</span>
													</button>
												</Tooltip>
											)}
										</>
									)}
								</>
							)}
							{patient?.nicu?.id && (
								<>
									{patient?.nicu?.start_discharge ? (
										<Tooltip title="Complete Discharge">
											<button className="btn btn-warning btn-sm mr-1" onClick={() => finishNicuDischarge(patient?.nicu?.id)}>
												<i className="fa fa-hospital-o"></i>
												<span style={{ marginLeft: '4px' }}>Finish Discharge</span>
											</button>
										</Tooltip>
									) : (
										<>
											{user.role.slug === 'doctor' || user.role.slug === 'it-admin' || user.role.slug === 'medical-director' ? (
												<Tooltip title="Discharge">
													<button className="btn btn-danger btn-sm mr-1" onClick={() => startNicuDischarge(patient?.nicu?.id)}>
														<i className="fa fa-hospital-o"></i>
														<span style={{ marginLeft: '4px' }}>Nicu Discharge</span>
													</button>
												</Tooltip>
											) : (
												<Tooltip title="A doctor should start the discharge process">
													<button className="btn btn-danger btn-sm mr-1">
														<i className="fa fa-hospital-o"></i>
														<span style={{ marginLeft: '4px' }}>Nicu Discharge</span>
													</button>
												</Tooltip>
											)}
										</>
									)}
								</>
							)}
						</>
				  )
				: canAdmit && (
						<Tooltip title="Admit">
							<Link to={`${location.pathname}#start-admission`} className="btn btn-primary btn-sm mr-1">
								<i className="os-icon os-icon-ui-22"></i>
								<span>Admit</span>
							</Link>
						</Tooltip>
				  )}
		</>
	);
};

export default DischargeBlock;
